import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useCompact } from "../hooks";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";

/**
 * The toolbar's element, owned by the shell (AppLayout) so the bar spans the
 * whole content column, and filled by whichever page is showing. A portal
 * rather than state lifted into the shell: the trail carries the page's own
 * click handlers, and pushing those up on every render would re-render the
 * shell that renders the page.
 */
const ToolbarSlot = createContext<HTMLElement | null>(null);
export const ToolbarSlotProvider = ToolbarSlot.Provider;

export interface Crumb {
  label: string;
  to: string;
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * A page's large title and the toolbar above it.
 *
 * The title scrolls away with the page, as a Mac window's content does, and
 * the toolbar keeps where you are: the trail back on a desk, the way back on
 * a phone, and the page's own name once the large one has passed under the
 * bar. Showing both at once would print the title twice; showing neither
 * would leave a reader at row 40 not knowing whose activity this is.
 */
export function PageHead({
  title,
  icon,
  trail = [],
  aside,
  children,
}: {
  title: string;
  /** A section's glyph beside its title. Omitted one level down, where the title is a member. */
  icon?: ReactNode;
  /** The pages above this one, outermost first. Empty for a section's own page. */
  trail?: Crumb[];
  /** The head's right-hand slot: a scope picker, a role control. */
  aside?: ReactNode;
  /** Under the title: what the page is, or who the member is. */
  children?: ReactNode;
}) {
  const compact = useCompact();
  const slot = useContext(ToolbarSlot);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const [titleGone, setTitleGone] = useState(false);

  useEffect(() => {
    const target = titleRef.current;
    if (!target || typeof IntersectionObserver !== "function") return;
    // The bar's own height off the top (--toolbar-height), so the title hands
    // over as it passes under the bar rather than off the screen.
    const observer = new IntersectionObserver(
      ([entry]) => setTitleGone(entry ? !entry.isIntersecting : false),
      { rootMargin: "-52px 0px 0px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const bar = (
    <nav
      className="toolbar-inner"
      aria-label="Breadcrumb"
      data-title={titleGone ? "shown" : "hidden"}
    >
      {compact ? <PhoneBar trail={trail} title={title} /> : <Trail trail={trail} title={title} />}
    </nav>
  );

  return (
    <>
      {slot ? createPortal(bar, slot) : null}
      <header className="page-head">
        <div className="stack page-head-text">
          <h1 ref={titleRef} className="page-title">
            {icon}
            <span className="title-wrap">{title}</span>
          </h1>
          {children}
        </div>
        {aside}
      </header>
    </>
  );
}

/**
 * The trail on a desk. The current page ends it, so the trail reads as a
 * path; on a section's own page that is the only crumb, which only echoes the
 * large title, so it waits until the title has scrolled away.
 */
function Trail({ trail, title }: { trail: Crumb[]; title: string }) {
  return (
    <ol className="crumbs">
      {trail.map((crumb) => (
        <li key={crumb.to}>
          <Link to={crumb.to} onClick={crumb.onClick}>
            {crumb.label}
          </Link>
          <ChevronRightIcon />
        </li>
      ))}
      <li
        aria-current="page"
        className={trail.length > 0 ? "crumb-current" : "crumb-current crumb-echo"}
      >
        {title}
      </li>
    </ol>
  );
}

/**
 * A phone's top bar: back to the page above, and the title in the middle once
 * the large one has gone. The placeholder keeps the title in the second
 * column when there is nowhere to go back to.
 */
function PhoneBar({ trail, title }: { trail: Crumb[]; title: string }) {
  const parent = trail[trail.length - 1];
  return (
    <>
      {parent ? (
        <Link to={parent.to} className="toolbar-back" onClick={parent.onClick}>
          <ChevronLeftIcon />
          {parent.label}
        </Link>
      ) : (
        <span />
      )}
      <span className="toolbar-title" aria-hidden="true">
        {title}
      </span>
    </>
  );
}
